import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Plus, RefreshCw, FileText,
  AlertCircle, BookCheck,
  ArrowUpRight, ArrowDownLeft
} from 'lucide-react';

import { chequeApi }        from '@/api/cheque.api';
import { useContextScope, scopeFromSession } from '@/hooks/useContextScope';
import { ScopeChips } from '@/components/lists/ScopeChips';
import { usePermission }    from '@/hooks/usePermission';
import { Permissions }      from '@/constants/permissions';
import { AppRoutes }        from '@/constants/routes';
import { Spinner }          from '@/components/ui/Spinner';
import { ListPager } from '@/components/lists/ListPager';
import { ListSearch, DateRangeFilter } from '@/components/lists/ListFilters';
import { cn }               from '@/utils/cn';
import type { RootState }   from '@/state/store';

import type {
  ChequeRecordListDto,
  ChequeRecordFilterDto,
  ChequeStatus,
  ChequeType,
} from '@/types/cheque.types';
import {
  CHEQUE_STATUS_LABELS,
  CHEQUE_STATUS_COLORS,
  CHEQUE_TYPE_LABELS,
} from '@/types/cheque.types';

// ── Cheque type badge ─────────────────────────────────────────────────────────

const ChequeTypeBadge = ({ type }: { type: ChequeType }) => (
  <span className={cn(
    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
    type === 'Issued'
      ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
      : type === 'Received'
        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
        : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  )}>
    {type === 'Issued' ? <ArrowUpRight size={10} /> : <ArrowDownLeft size={10} />}
    {CHEQUE_TYPE_LABELS[type]}
  </span>
);

// ── Status icon helper ────────────────────────────────────────────────────────

const StatusIcon = ({ status }: { status: ChequeStatus }) => {
  const props = { size: 14, className: 'shrink-0' };
  switch (status) {
    case 'Draft':     return <FileText  {...props} className={`${props.className} text-gray-500 dark:text-gray-400`} />;
    case 'Cleared':   return <BookCheck {...props} className={`${props.className} text-green-600`} />;
    case 'Returned':  return <AlertCircle {...props} className={`${props.className} text-red-500`} />;
    default:          return <AlertCircle {...props} className={`${props.className} text-amber-500`} />;
  }
};

// ── Cheque List Page ──────────────────────────────────────────────────────────

export const ChequeListPage = () => {
  const navigate              = useNavigate();
  const { hasPermission }     = usePermission();
  const canCreate             = hasPermission(Permissions.BNK_CHEQUE_CREATE);

  const session = useSelector((s: RootState) => s.session.session);

  const [items,   setItems]   = useState<ChequeRecordListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const statusCounts = {
    Pending:   items.filter(i => i.chequeStatus === 'Draft' || i.chequeStatus === 'Received' || i.chequeStatus === 'Issued').length,
    Deposited: items.filter(i => i.chequeStatus === 'Deposited' || i.chequeStatus === 'Presented').length,
    Cleared:   items.filter(i => i.chequeStatus === 'Cleared').length,
    Returned:  items.filter(i => i.chequeStatus === 'Returned').length,
    Cancelled: items.filter(i => i.chequeStatus === 'Cancelled').length,
  };

  const [filter, setFilter] = useState<ChequeRecordFilterDto>(() => ({
    page:       1,
    pageSize,
    ...scopeFromSession(session, ['locationId']),
  }));

  const scope = useContextScope(s => {
    setFilter(f => ({ ...f, ...s, page: 1 }));
    setPage(1);
  }, ['locationId']);

  const [search, setSearch] = useState('');

  const load = useCallback(async (f: ChequeRecordFilterDto) => {
    setLoading(true);
    try {
      const res  = await chequeApi.getList(f);
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(data?.totalCount ?? 0);
    } catch (err) {
      console.error('Failed to load cheques', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const handleSearch = () => {
    setFilter(f => ({ ...f, search: search || undefined, page: 1 }));
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilter(f => ({ ...f, page: newPage }));
  };

  const handleStatusFilter = (status: string) => {
    setFilter(f => ({
      ...f,
      status: f.status === status ? undefined : status,
      page:   1,
    }));
    setPage(1);
  };

  const totalPages = Math.ceil(total / pageSize);


  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  // Resolve payee/payer display from customer or supplier
  const getPartyName = (item: ChequeRecordListDto): string => {
    if (item.customerName) return item.customerName;
    if (item.supplierName) return item.supplierName;
    return '—';
  };

  const getPartyCode = (item: ChequeRecordListDto): string | null => {
    if (item.customerCode) return item.customerCode;
    if (item.supplierCode) return item.supplierCode;
    return null;
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* ── Page Header ──────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
              Cheque Register
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {total.toLocaleString()} total records
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load(filter)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.BANK_CHEQUE_NEW)}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus size={15} />
                New Cheque
              </button>
            )}
          </div>
        </div>

        {/* ── Context chips ─────────────────────────────────────────────────── */}
        <ScopeChips chips={scope.chips} canReset={scope.canReset} onReset={scope.reset} />

        {/* ── Status Summary Bar ────────────────────────────────────────────── */}
        <div className="grid grid-cols-5 gap-2 mt-4">
          {([
            { label: 'Pending',   count: statusCounts.Pending,   color: 'text-slate-500 dark:text-gray-400',  bg: 'bg-slate-50 dark:bg-slate-700/40',   ring: 'ring-slate-200',  clickStatus: 'Draft'     },
            { label: 'Deposited', count: statusCounts.Deposited, color: 'text-blue-600 dark:text-blue-400',   bg: 'bg-blue-50 dark:bg-blue-900/20',     ring: 'ring-blue-200',   clickStatus: 'Deposited' },
            { label: 'Cleared',   count: statusCounts.Cleared,   color: 'text-green-600 dark:text-green-400',  bg: 'bg-green-50 dark:bg-green-900/20',   ring: 'ring-green-200',  clickStatus: 'Cleared'   },
            { label: 'Returned',  count: statusCounts.Returned,  color: 'text-red-500',    bg: 'bg-red-50 dark:bg-red-900/20',       ring: 'ring-red-200',    clickStatus: 'Returned'  },
            { label: 'Cancelled', count: statusCounts.Cancelled, color: 'text-gray-500 dark:text-gray-400',   bg: 'bg-gray-50 dark:bg-gray-700/40',     ring: 'ring-gray-200',   clickStatus: 'Cancelled' },
          ] as const).map(stat => {
            const isActive = filter.status === stat.clickStatus;
            return (
              <button
                key={stat.label}
                onClick={() => handleStatusFilter(stat.clickStatus)}
                title={isActive ? `Clear "${stat.label}" filter` : `Show only ${stat.label}`}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg ring-1 transition-all text-left',
                  'hover:ring-2 hover:shadow-sm active:scale-95',
                  stat.bg, stat.ring,
                  isActive && 'ring-2 shadow-md scale-[0.98]',
                )}
              >
                <div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-none">{stat.label}</p>
                  <p className={cn('text-sm font-bold leading-tight', stat.color)}>{stat.count}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Filter row ────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <ListSearch
            value={search}
            onChange={setSearch}
            onSubmit={handleSearch}
            placeholder="Search cheque #, payee, bank…"
          />

          {/* Cheque type filter */}
          <select
            value={filter.chequeType ?? ''}
            onChange={e => { setFilter(f => ({ ...f, chequeType: e.target.value || undefined, pageNumber: 1 })); setPage(1); }}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2
                       bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200
                       focus:ring-2 focus:ring-teal-500 outline-none"
          >
            <option value="">All Types</option>
            <option value="Issued">Issued</option>
            <option value="Received">Received</option>
            <option value="PostDated">Post-Dated</option>
          </select>

          {/* Status pills */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {(['Draft', 'Received', 'Issued', 'Deposited', 'Presented', 'Cleared', 'Returned', 'Cancelled', 'Endorsed'] as const).map(s => (
              <button
                key={s}
                onClick={() => handleStatusFilter(s)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-full transition-all',
                  filter.status === s
                    ? (CHEQUE_STATUS_COLORS[s] ?? '') + ' ring-2 ring-offset-1 ring-current'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                )}
              >
                {CHEQUE_STATUS_LABELS[s]}
              </button>
            ))}
          </div>

          {/* Date range */}
          <DateRangeFilter
            from={filter.dateFrom}
            to={filter.dateTo}
            onChange={({ from, to }) => {
              setFilter(f => ({ ...f, dateFrom: from, dateTo: to, pageNumber: 1 }));
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <FileText size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No cheque records found</p>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.BANK_CHEQUE_NEW)}
                className="mt-3 text-teal-600 text-sm hover:underline"
              >
                Add your first cheque
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Cheque #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Bank Account</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Book</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Drawer Bank</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Payee / Payer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Ccy</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Reference</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(chq => (
                <tr
                  key={chq.id}
                  onClick={() => navigate(AppRoutes.BANK_CHEQUE_DETAIL.replace(':id', chq.id))}
                  className="hover:bg-teal-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors group"
                >
                  <td className="px-4 py-3 font-mono font-semibold text-teal-700 dark:text-teal-400 group-hover:text-teal-800">
                    {chq.chequeNumber}
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                    {fmtDate(chq.chequeDate)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 dark:text-white">{chq.bankAccountCode ?? '—'}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[160px]">
                      {/* No bank named yet — a received cheque posted to Cheques in Hand (GAP-013) */}
                      {chq.bankAccountName ?? 'Not yet deposited'}
                    </div>
                  </td>
                  {/* Which book the number came from (GAP-001). A dash is the honest answer for a
                      received cheque — it is the drawer's stationery, not ours. */}
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-400">
                    {chq.chequeBookCode ?? '—'}
                  </td>
                  {/* The customer's bank the cheque is drawn on (GAP-014) — blank for one we issued,
                      which is our own stationery, not the drawer's. */}
                  <td className="px-4 py-3">
                    <div className="text-slate-900 dark:text-white text-xs">{chq.drawerBankName ?? '—'}</div>
                    {chq.drawerBranchName && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[140px]">
                        {chq.drawerBranchName}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {getPartyCode(chq) && (
                      <div className="font-medium text-slate-900 dark:text-white text-xs">{getPartyCode(chq)}</div>
                    )}
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[160px]">{getPartyName(chq)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <ChequeTypeBadge type={chq.chequeType} />
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 font-mono text-xs">
                    {chq.currencyCode}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">
                    {chq.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      <StatusIcon status={chq.chequeStatus} />
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        CHEQUE_STATUS_COLORS[chq.chequeStatus]
                      )}>
                        {CHEQUE_STATUS_LABELS[chq.chequeStatus]}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs truncate max-w-[120px]">
                    {documentNo(chq.referenceDocument)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ───────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <ListPager
          page={page}
          totalPages={totalPages}
          totalCount={total}
          onChange={handlePageChange}
          noun="cheques"
        />
      )}
    </div>
  );
};

/** POS references arrive as "<outlet key>/INV-0000442"; show only the bill number. */
function documentNo(reference?: string | null): string {
  if (!reference) return '—';
  const i = reference.lastIndexOf('/');
  return i >= 0 ? reference.slice(i + 1) : reference;
}
